import { useState } from 'react';
import {
  HomefieldIconGgLogoBlack,
  HomefieldIconPlLogoBlack,
  HomefieldIconGggLogoBlack,
  HomefieldIconGgLogoHover,
  HomefieldIconPlLogoHover,
  HomefieldIconGggLogoHover,
} from '@dsg-tech/homefield-react-icons';
import { CTA, NavBarData, NikeConnected, StoreIdentifierName } from 'types';
import { setDataEmTag, checkStores } from 'utility/utility';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import { multiUseAnalyticsEvent } from 'utility/analytics-events';
import { TextLink } from '@dsg-tech/homefield-react';
import TopMessage from './TopMessage';
import './TopNavBar.scss';

type Props = {
  navBarCtas: NavBarData[];
  storeIdentifierName: StoreIdentifierName;
  topMessageData: [CTA] | undefined;
  nikeConnected: NikeConnected | null;
  favoritesIconEnabled: boolean;
  redirectToOtherVerticalsEnabled: boolean;
};

export const NoIcon = () => null;
export const createIcon = (name: string, isHovered: boolean) => {
  if (isHovered) {
    switch (name) {
      case 'publiclands':
        return HomefieldIconPlLogoHover;
      case 'golf_galaxy':
        return HomefieldIconGgLogoHover;
      case 'g3':
        return HomefieldIconGggLogoHover;
      default:
        return NoIcon;
    }
  } else {
    switch (name) {
      case 'publiclands':
        return HomefieldIconPlLogoBlack;
      case 'golf_galaxy':
        return HomefieldIconGgLogoBlack;
      case 'g3':
        return HomefieldIconGggLogoBlack;
      default:
        return NoIcon;
    }
  }
};

const brandsWithLogo = [
  {
    name: 'publiclands',
    link: 'https://www.publiclands.com',
  },
  {
    name: 'golf_galaxy',
    link: 'https://www.golfgalaxy.com',
  },
  {
    name: 'g3',
    link: 'https://www.goinggoinggone.com',
  },
];

export default function TopNavBar({
  navBarCtas,
  topMessageData,
  storeIdentifierName,
  nikeConnected,
  redirectToOtherVerticalsEnabled,
  favoritesIconEnabled,
}: Readonly<Props>) {
  const [hoveredIcon, setHoveredIcon] = useState('');

  // Use inverse variant for Public Lands due to the dark background
  const linkVariant = storeIdentifierName === 'pl' ? 'group-inverse' : 'group';

  const findLastThreeLinks = (i: number) => {
    const isFavoriteLinkAvailable = navBarCtas.some((element) => element.linkTitle === 'Favorites');
    const setLastItems = isFavoriteLinkAvailable ? 3 : 2;
    const lastIndex = navBarCtas?.length - setLastItems;

    if (i === lastIndex) {
      return ' border-left bold-text hmf-body-bold-s';
    }
    if (i > lastIndex) {
      return ' bold-text hmf-body-bold-s';
    }

    return '';
  };

  return (
    <div data-testid="TopNavBar" className="topNavBar-container">
      <div className="topNavBar-container__content-wrapper">
        {redirectToOtherVerticalsEnabled && (
          <div className="enterprise-brand-links hmf-pl-s hmf-display-flex hmf-align-items-center hmf-flex-wrap hmf-justify-content-space-around hmf-align-content-center">
            {brandsWithLogo.map(({ name, link }) => {
              const IconComponent = createIcon(name, hoveredIcon === name);
              return (
                <TextLink
                  key={name}
                  href={`${link}?camp=DRT:HeaderLink:${storeIdentifierName.toUpperCase()}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={name}
                  variant={linkVariant}
                  size="small">
                  <IconComponent
                    className={`header2-pl-other-brands-logo ${name}`}
                    onMouseOver={() => setHoveredIcon(name)}
                    onFocus={() => setHoveredIcon(name)}
                    onMouseOut={() => setHoveredIcon('')}
                    onBlur={() => setHoveredIcon('')}
                  />
                </TextLink>
              );
            })}
          </div>
        )}
        {topMessageData && (
          <ErrorBoundaryWithLogger componentId="TopNavBar-TopMessage">
            <TopMessage topMessageData={topMessageData} storeIdentifierName={storeIdentifierName} />
          </ErrorBoundaryWithLogger>
        )}
        <div className="topNavBar-container__text-container hmf-py-xs hmf-display-flex hmf-flex-row hmf-justify-content-flex-end">
          {nikeConnected && (
            <TextLink
              key="link-dicks-nike"
              href="/f/all-nike-connected-membership"
              target="_self"
              title="DICK'S | Nike"
              data-em={setDataEmTag('Header', 'header_dicks_nike')}
              className="topNavBar-container__text-container--links hmf-body-s hmf-m-0 hmf-px-s"
              variant={linkVariant}
              size="small">
              DICK&#39;S | Nike
              <div className="hidden-item">DICK&#39;S | Nike</div>
            </TextLink>
          )}

          {navBarCtas
            .filter((links) => (favoritesIconEnabled ? links : links.linkTitle !== 'Favorites'))
            .map((links: CTA, index: number) => (
              <TextLink
                key={`link-${links.linkTitle}`}
                href={checkStores(links.link)}
                onClick={() =>
                  links.linkTitle === 'Favorites'
                    ? multiUseAnalyticsEvent({
                        event: 'Signed out athlete clicked Favorites from:',
                        value: 'Header',
                      })
                    : {}
                }
                target={checkStores(links.link)?.includes('http') ? '_blank' : '_self'}
                title={links.linkTitle}
                data-em={setDataEmTag('Header', links.linkTitle)}
                className={`topNavBar-container__text-container--links hmf-body-s hmf-m-0 hmf-px-s${findLastThreeLinks(
                  index,
                )}`}
                rel={checkStores(links.link)?.includes('http') ? 'noreferrer' : ''}
                variant={linkVariant}
                size="small">
                {links.linkTitle}
                <div className="hidden-item hmf-body-bold-s">{links.linkTitle}</div>
              </TextLink>
            ))}
        </div>
      </div>
    </div>
  );
}
